"use client"; // Add this for client-side hooks and interaction

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from "next/link";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Menu, X, Home, Gamepad2, Users, Trophy, User as UserIcon, LogIn, LogOut, ChevronDown, Sparkles, BarChart, Shuffle, MessageCircle } from 'lucide-react';
import { createClient } from '@/utils/supabase/client';
import React from 'react';
import type{ User } from '@supabase/supabase-js';

// 라우트 타입 정의
interface Route {
  href: string;
  label: string;
  icon?: React.ReactNode;
  isUserGreeting?: boolean;
  onClick?: () => void;
  isSubLink?: boolean; // 서브 링크 여부 표시
}

// SiteHeaderProps 인터페이스 추가
interface SiteHeaderProps {
  topCategories?: string[];
  user: User | null;
}

export function SiteHeader({ topCategories, user }: SiteHeaderProps) {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // 로그아웃 처리
  const handleLogout = async () => {
    try {
      await createClient().auth.signOut();
      window.location.href = '/';
    } catch (error) {
      console.error('로그아웃 오류:', error);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery(""); // Clear input after search
      setIsMobileSearchOpen(false); // Close mobile search if open
    }
  };

  const toggleMobileSearch = () => {
    setIsMobileSearchOpen(!isMobileSearchOpen);
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (isMobileSearchOpen) setIsMobileSearchOpen(false);
  };

  // 메인 메뉴 항목 정의 (신규, 인기, 랜덤 포함)
  const mainRoutes: Route[] = [
    { href: "/bbs", label: "커뮤니티", icon: <Users size={18} /> },
    { href: "/chatroom", label: "채팅", icon: <MessageCircle size={18} /> },
    { href: "/ranking", label: "랭킹", icon: <Trophy size={18} /> },
    // 신규, 인기, 랜덤 게임 링크 추가
    { href: "/games/new", label: "신규", icon: <Sparkles size={18} />, isSubLink: true },
    { href: "/games/popular", label: "인기", icon: <BarChart size={18} />, isSubLink: true },
    { href: "/games/random", label: "랜덤", icon: <Shuffle size={18} />, isSubLink: true },
  ];

  // 서브 메뉴 항목 생성 (사용자 정보 및 인기 카테고리)
  const getSubRoutes = (): Route[] => {
    const categoryRoutes: Route[] = topCategories && topCategories.length > 0
      ? topCategories.slice(0, 6).map(category => ({
          href: `/category/${encodeURIComponent(category)}`,
          label: category,
          isSubLink: true, // 서브 링크로 표시
        }))
      : [];

    let userRoutes: Route[] = [];
    if (user) {
      userRoutes = [
        { href: "/account", label: `${user.user_metadata?.username || user.email?.split('@')[0] || '사용자'}님`, icon: <UserIcon size={18} />, isUserGreeting: true },
        { href: "#", label: "로그아웃", icon: <LogOut size={18} />, onClick: handleLogout }
      ];
    } else {
      userRoutes = [
        { href: "/account", label: "로그인", icon: <LogIn size={18} /> }
      ];
    }
    // 구분자 로직 수정: 필요한 곳에만 명시적으로 추가
    let combinedRoutes: Route[] = [];
    if (categoryRoutes.length > 0) {
        combinedRoutes = [...combinedRoutes, ...categoryRoutes];
    }
    if (userRoutes.length > 0) {
        if (combinedRoutes.length > 0) { // 카테고리가 있으면 구분자 추가
             combinedRoutes.push({ href: "#sep1", label: "|", onClick: () => {} });
        }
        combinedRoutes = [...combinedRoutes, ...userRoutes];
    }
    return combinedRoutes;
  };

  const subRoutes = getSubRoutes();

  return (
    <header className="fixed sticky top-0 z-50 w-screen bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {/* 첫 번째 단: 로고와 메인 메뉴 (신규, 인기, 랜덤 포함) */}
      <div className="border-b border-border/40">
        <div className="container flex h-16 max-w-screen-xl items-center justify-between">
          {/* 로고 */}
          <Link href="/" className="flex items-center space-x-2">
            <img
              src="/kz.png"
              width={128}
              height={48}
              alt="키즈짱게임 로고"
              className="inline-block"
            />
          </Link>

          {/* 데스크톱 메인 메뉴 (통합됨) */}
          <nav className="hidden md:flex items-center space-x-3 lg:space-x-4 overflow-x-auto scrollbar-hide">
            {mainRoutes.map((route, index) => (
              <React.Fragment key={route.href}>
                <Link
                  href={route.href}
                  className={`flex items-center space-x-1 text-sm transition-colors hover:text-orange-600 dark:hover:text-orange-400 whitespace-nowrap ${
                    route.isSubLink ? 'font-medium text-muted-foreground' : 'font-bold'
                  }`}
                >
                  {route.icon && <span className="hidden lg:inline mr-1">{route.icon}</span>}
                  <span>{route.label}</span>
                </Link>
              </React.Fragment>
            ))}
          </nav>

          {/* 사용자 정보/로그인/아웃 - 1단 우측 버튼 */}
          <div className="hidden md:flex items-center space-x-2 ml-4">
            {user ? (
              <>
                <Link href="/account" className="flex items-center px-3 py-1 rounded-full bg-orange-100 text-orange-700 font-semibold border border-orange-200 hover:bg-orange-200 transition">
                  <UserIcon size={18} className="mr-1" />
                  <span>{user.user_metadata?.username || user.email?.split('@')[0] || '사용자'}님</span>
                </Link>
                <button onClick={handleLogout} className="flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-700 font-semibold border border-gray-200 hover:bg-gray-200 transition">
                  <LogOut size={18} className="mr-1" />
                  <span>로그아웃</span>
                </button>
              </>
            ) : (
              <Link href="/account" className="flex items-center px-3 py-1 rounded-full bg-orange-100 text-orange-700 font-semibold border border-orange-200 hover:bg-orange-200 transition">
                <LogIn size={18} className="mr-1" />
                <span>로그인</span>
              </Link>
            )}
          </div>

          {/* 모바일 메뉴 토글 버튼 */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMobileMenu}
            className="md:hidden"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            <span className="sr-only">메뉴 토글</span>
          </Button>
        </div>
      </div>

      {/* 두 번째 단: 카테고리 버튼만 가운데 정렬 */}
      <div className="border-b border-border/40 bg-orange-50/50 dark:bg-orange-950/20">
        <div className="container flex h-12 max-w-screen-2xl items-center justify-center">
          {/* 데스크톱 카테고리 버튼 (최대 6개, 가운데 정렬) */}
          <div className="hidden md:flex flex-1 justify-center space-x-2">
            {subRoutes.filter(route => route.isSubLink).map((route) => (
              <Link
                key={route.href}
                href={route.href}
                className="px-3 py-1 rounded-full bg-orange-100 text-orange-700 font-medium border border-orange-200 hover:bg-orange-200 transition flex items-center"
              >
                <span>{route.label}</span>
              </Link>
            ))}
          </div>
          {/* 데스크톱 검색 폼 */}
          <form onSubmit={handleSearchSubmit} className="hidden md:flex items-center relative w-full max-w-xs lg:max-w-sm ml-auto">
            <Input
              type="search"
              placeholder="게임 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 pl-8 pr-4 rounded-full border border-input bg-white dark:bg-gray-800 focus-visible:ring-offset-0 focus-visible:ring-orange-500 focus-visible:ring-1"
            />
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          </form>
          {/* 모바일 검색 토글 버튼 */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMobileSearch}
            className="md:hidden ml-auto"
          >
            <Search className="h-5 w-5" />
            <span className="sr-only">검색 토글</span>
          </Button>
        </div>
      </div>

      {/* 모바일 검색 입력 - 토글 시 표시 */}
      {isMobileSearchOpen && (
        <div className="container md:hidden py-2 bg-background border-b">
          <form onSubmit={handleSearchSubmit} className="flex items-center relative">
            <Input
              type="search"
              placeholder="게임 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-10 pl-10 pr-4 w-full rounded-full border border-input bg-white dark:bg-gray-800 focus-visible:ring-offset-0 focus-visible:ring-orange-500 focus-visible:ring-1"
              autoFocus
            />
            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          </form>
        </div>
      )}

      {/* 모바일 메뉴 (통합됨) */}
      {isMobileMenuOpen && (
        <div className="container md:hidden py-4 bg-background border-b">
           <nav className="grid gap-2">
             {/* 모든 메인 메뉴 항목 */}
             {mainRoutes.map((route) => (
               <Link key={route.href} href={route.href} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center space-x-2 p-2 rounded hover:bg-accent">
                 {route.icon} <span>{route.label}</span>
               </Link>
             ))}
             <hr className="my-2"/>
             {/* 인기 카테고리 */}
             {topCategories && topCategories.length > 0 && (
               <>
                 <span className="px-2 py-1 text-xs font-semibold text-muted-foreground">인기 카테고리</span>
                 {topCategories.slice(0, 6).map((category) => (
                   <Link
                     key={category}
                     href={`/category/${encodeURIComponent(category)}`}
                     onClick={() => setIsMobileMenuOpen(false)}
                     className="flex items-center space-x-2 p-2 rounded hover:bg-accent text-sm"
                   >
                     <span># {category}</span>
                   </Link>
                 ))}
                 <hr className="my-2"/>
               </>
             )}
             {/* 로그인/아웃 */}
             {user ? (
               <>
                 <Link href="/account" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center space-x-2 p-2 rounded hover:bg-accent">
                   <UserIcon size={18} /> <span>{user.user_metadata?.username || user.email?.split('@')[0] || '사용자'}님</span>
                 </Link>
                 <button onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }} className="flex items-center space-x-2 p-2 rounded hover:bg-accent text-left">
                   <LogOut size={18} /> <span>로그아웃</span>
                 </button>
               </>
             ) : (
               <Link href="/account" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center space-x-2 p-2 rounded hover:bg-accent">
                 <LogIn size={18} /> <span>로그인</span>
               </Link>
             )}
           </nav>
        </div>
      )}

      <style jsx>{`
        /* 오렌지 기반 다채롭고 선명한 디자인 */
        header {
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }
        
        /* 첫 번째 단 스타일 */
        header > div:first-of-type {
          background: linear-gradient(to right, rgba(255, 255, 255, 0.95), rgba(255, 247, 237, 0.95));
        }
        
        :global(.dark) header > div:first-of-type {
          background: linear-gradient(to right, rgba(17, 24, 39, 0.95), rgba(30, 41, 59, 0.95));
        }
        
        /* 두 번째 단 스타일 */
        header > div:nth-of-type(2) {
          background: linear-gradient(to right, rgba(255, 247, 237, 0.7), rgba(255, 237, 213, 0.7));
        }
        
        :global(.dark) header > div:nth-of-type(2) {
          background: linear-gradient(to right, rgba(30, 41, 59, 0.7), rgba(15, 23, 42, 0.7));
        }
        
        /* 로고 텍스트 스타일 */
        header a:first-of-type span {
          background: linear-gradient(45deg, #f97316, #ea580c);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          text-fill-color: transparent;
        }
        
        :global(.dark) header a:first-of-type span {
          background: linear-gradient(45deg, #fb923c, #f97316);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          text-fill-color: transparent;
        }
        
        /* 메뉴 호버 효과 */
        header nav a {
          position: relative;
        }
        
        header nav a:not(:last-child)::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 0;
          width: 0;
          height: 2px;
          background: linear-gradient(90deg, #f97316, #ea580c);
          transition: width 0.3s ease;
        }
        
        :global(.dark) header nav a:not(:last-child)::after {
          background: linear-gradient(90deg, #fb923c, #f97316);
        }
        
        header nav a:hover::after {
          width: 100%;
        }
        
        /* 검색 입력 스타일 */
        header input[type="search"] {
          transition: all 0.3s ease;
        }
        
        header input[type="search"]:focus {
          box-shadow: 0 0 0 2px rgba(249, 115, 22, 0.2);
        }
        
        :global(.dark) header input[type="search"]:focus {
          box-shadow: 0 0 0 2px rgba(251, 146, 60, 0.2);
        }
        
        /* 사용자 환영 메시지 스타일 */
        .user-greeting {
          position: relative;
          padding-right: 1rem;
        }
        
        .user-greeting::after {
          content: '';
          position: absolute;
          right: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 1px;
          height: 1rem;
          background-color: rgba(0, 0, 0, 0.1);
        }
        
        :global(.dark) .user-greeting::after {
          background-color: rgba(255, 255, 255, 0.1);
        }
        
        /* 모바일 메뉴 스타일 */
        @media (max-width: 768px) {
          header > div:first-of-type {
            background: linear-gradient(to right, rgba(255, 255, 255, 0.98), rgba(255, 247, 237, 0.98));
          }
          
          :global(.dark) header > div:first-of-type {
            background: linear-gradient(to right, rgba(17, 24, 39, 0.98), rgba(30, 41, 59, 0.98));
          }
        }
      `}</style>
    </header>
  );
}

